import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  InputBase,
  Avatar,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import StatsPanel from "./StatsPanel";
import CategoryList from "./CategoryList";
import MenuItemCard from "./MenuItemCard";

/**
 * ConfirmMenu - Step 4: Review and confirm OCR-parsed menu data
 *
 * @param {Object}   menuData       - Full parsed menu data from OCR API
 * @param {string}   currencySymbol - Currency symbol from business location
 * @param {Object}   planLimits     - { maxCategories, maxItems } or null (unlimited)
 * @param {string}   isSubmitting   - null | "draft" | "confirm"
 * @param {Function} onSaveDraft    - (selectionData) => void
 * @param {Function} onConfirm      - (selectionData) => void
 * @param {Function} onUpgrade      - () => void
 */
const ConfirmMenu = ({
  isCatalog = false,
  menuData,
  currencySymbol = "$",
  planLimits,
  existingCategoryCount = 0,
  existingItemCount = 0,
  isSubmitting,
  onSaveDraft,
  onConfirm,
  onUpgrade,
}) => {
  const label = isCatalog ? "catalogue" : "menu";
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    menuData.categories[0]?.id || null
  );
  const [menuName, setMenuName] = useState(menuData.menuName);
  const [isEditingName, setIsEditingName] = useState(false);
  // Local mutable copy of categories for inline edits & deletes
  const [categories, setCategories] = useState(menuData.categories);

  // Remaining slots = planLimit - existingDBCount (how many MORE can be added)
  const remainingCatSlots = planLimits?.maxCategories != null
    ? Math.max(0, planLimits.maxCategories - existingCategoryCount)
    : null;
  const remainingItemSlots = planLimits?.maxItems != null
    ? Math.max(0, planLimits.maxItems - existingItemCount)
    : null;

  // Checkboxes — pre-check only up to remaining slots (Option B)
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(() => {
    const cats = remainingCatSlots !== null
      ? menuData.categories.slice(0, remainingCatSlots)
      : menuData.categories;
    return new Set(cats.map((c) => c.id));
  });
  const [selectedItemIds, setSelectedItemIds] = useState(() => {
    const allowedCats = remainingCatSlots !== null
      ? menuData.categories.slice(0, remainingCatSlots)
      : menuData.categories;

    let itemCounter = 0;
    return new Map(
      menuData.categories.map((cat) => {
        const isCatAllowed = allowedCats.some((c) => c.id === cat.id);
        if (!isCatAllowed) return [cat.id, new Set()];

        const allowedItems = remainingItemSlots !== null
          ? cat.items.filter(() => {
              if (itemCounter < remainingItemSlots) { itemCounter++; return true; }
              return false;
            })
          : cat.items;
        return [cat.id, new Set(allowedItems.map((i) => i.id))];
      })
    );
  });

  // ── Derived counts ───────────────────────────────────────────────────────
  const totalCategories = categories.length;
  const selectedCatCount = selectedCategoryIds.size;
  const totalItems = categories.reduce((sum, cat) => sum + cat.items.length, 0);
  const selectedItemCount = [...selectedCategoryIds].reduce(
    (sum, catId) => sum + (selectedItemIds.get(catId)?.size || 0),
    0
  );
  const maxCats = planLimits?.maxCategories ?? null;
  // Over limit if existing + selected exceeds plan max
  const isOverCatLimit = maxCats !== null && (existingCategoryCount + selectedCatCount) > maxCats;

  // Get items for selected category
  const selectedCategory = categories.find((cat) => cat.id === selectedCategoryId);
  const items = selectedCategory?.items || [];

  // ── Checkbox handlers ────────────────────────────────────────────────────
  const handleToggleCategory = (e, catId) => {
    e.stopPropagation();
    const isCurrentlyChecked = selectedCategoryIds.has(catId);

    // If checking and remaining slots are exhausted → show upgrade dialog
    if (!isCurrentlyChecked && remainingCatSlots !== null && selectedCatCount >= remainingCatSlots) {
      onUpgrade?.(existingCategoryCount + selectedCatCount, "categories");
      return;
    }

    // Toggle category checked state
    setSelectedCategoryIds((prev) => {
      const next = new Set(prev);
      if (isCurrentlyChecked) next.delete(catId);
      else next.add(catId);
      return next;
    });
    // Uncheck all items in category when unchecking, re-check all when checking back
    setSelectedItemIds((prev) => {
      const next = new Map(prev);
      const cat = categories.find((c) => c.id === catId);
      if (isCurrentlyChecked) {
        // Uncheck all items
        next.set(catId, new Set());
      } else {
        // Re-check all items
        next.set(catId, new Set(cat?.items.map((i) => i.id) || []));
      }
      return next;
    });
  };

  const handleToggleItem = (itemId) => {
    const isCurrentlyChecked =
      selectedItemIds.get(selectedCategoryId)?.has(itemId) ?? false;

    // If checking (not unchecking), verify item plan limit against remaining slots
    if (!isCurrentlyChecked && remainingItemSlots !== null && selectedItemCount >= remainingItemSlots) {
      onUpgrade?.(existingItemCount + selectedItemCount, "items");
      return;
    }

    setSelectedItemIds((prev) => {
      const next = new Map(prev);
      const catSet = new Set(next.get(selectedCategoryId) || []);
      if (catSet.has(itemId)) catSet.delete(itemId);
      else catSet.add(itemId);
      next.set(selectedCategoryId, catSet);

      // Auto-check category if at least one item is checked; uncheck if none
      const cat = categories.find((c) => c.id === selectedCategoryId);
      const allItemIds = cat?.items.map((i) => i.id) || [];
      const anyChecked = allItemIds.some((id) => catSet.has(id));
      setSelectedCategoryIds((prevCats) => {
        const nextCats = new Set(prevCats);
        if (anyChecked) nextCats.add(selectedCategoryId);
        else nextCats.delete(selectedCategoryId);
        return nextCats;
      });

      return next;
    });
  };

  // ── Item edit/delete handlers ────────────────────────────────────────────
  const handleDeleteItem = (itemId) => {
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        items: cat.items.filter((i) => i.id !== itemId),
      }))
    );
    setSelectedItemIds((prev) => {
      const next = new Map(prev);
      for (const [catId, set] of next) {
        if (set.has(itemId)) {
          const newSet = new Set(set);
          newSet.delete(itemId);
          next.set(catId, newSet);
          break;
        }
      }
      return next;
    });
  };

  const handleItemUpdate = (itemId, updatedFields) => {
    setCategories((prev) =>
      prev.map((cat) => ({
        ...cat,
        items: cat.items.map((i) =>
          i.id === itemId ? { ...i, ...updatedFields } : i
        ),
      }))
    );
  };

  const buildSelectionData = () => ({
    selectedCategoryIds,
    selectedItemIds,
    menuName,
    categories,
  });

  return (
    <Box>
      {/* Title */}
      <Typography
        sx={{
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          fontWeight: 700,
          color: "#111827",
          textTransform: "lowercase",
          mb: 0.25,
        }}
      >
        confirm {label} details
      </Typography>
      <Typography
        sx={{
          fontSize: "0.8rem",
          color: "#6b7280",
          textTransform: "lowercase",
          mb: 2.5,
        }}
      >
        digitize your physical {label} in seconds
      </Typography>

      {/* Main layout: Left panel + Right panel */}
      <Box
        sx={{
          display: "flex",
          gap: 2.5,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* ── Left Panel ───────────────────────────────────────────────────── */}
        <Box
          sx={{
            width: { xs: "100%", md: 240 },
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Stats + AI Summary */}
          <StatsPanel stats={menuData.stats} aiSummary={menuData.aiSummary} />

          {/* Plan banner */}
          {isOverCatLimit ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "#fff",
                border: "1px solid #C4B5FD",
                borderRadius: 2,
                px: 1.5,
                py: 1.25,
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 16, color: "#7C3AED", flexShrink: 0 }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#111827",
                    textTransform: "lowercase",
                  }}
                >
                  unlock more menu flexibility
                </Typography>
                <Typography
                  sx={{ fontSize: "0.68rem", color: "#6b7280", textTransform: "lowercase" }}
                >
                  {existingCategoryCount + selectedCatCount}/{maxCats} categories · {existingItemCount + selectedItemCount}/{planLimits?.maxItems ?? totalItems} items
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                onClick={onUpgrade}
                sx={{
                  bgcolor: "#7C3AED",
                  color: "#fff",
                  textTransform: "lowercase",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: 1,
                  py: 0.25,
                  fontSize: "0.68rem",
                  flexShrink: 0,
                  minWidth: "auto",
                  "&:hover": { bgcolor: "#6200EE" },
                }}
              >
                upgrade now
              </Button>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "#FFFBEB",
                border: "1px solid #FDE68A",
                borderRadius: 2,
                px: 1.5,
                py: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  bgcolor: "#F59E0B",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography sx={{ fontSize: "0.55rem", color: "#fff", lineHeight: 1 }}>
                  ✓
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    color: "#92400E",
                    textTransform: "lowercase",
                  }}
                >
                  your {label} is ready to go
                </Typography>
                <Typography
                  sx={{ fontSize: "0.68rem", color: "#B45309", textTransform: "lowercase" }}
                >
                  {selectedCatCount}{remainingCatSlots !== null ? `/${remainingCatSlots}` : ""} new categories · {selectedItemCount}{remainingItemSlots !== null ? `/${remainingItemSlots}` : ""} new items added
                </Typography>
              </Box>
            </Box>
          )}

          {/* Category list with checkboxes */}
          <CategoryList
            categories={categories}
            selectedCategoryId={selectedCategoryId}
            onSelectCategory={setSelectedCategoryId}
            selectedCategoryIds={selectedCategoryIds}
            onToggleCategory={handleToggleCategory}
          />

          {/* Action buttons */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => onSaveDraft?.(buildSelectionData())}
              disabled={!!isSubmitting}
              sx={{
                borderColor: "#7C3AED",
                color: "#7C3AED",
                bgcolor: "#fff",
                textTransform: "lowercase",
                fontWeight: 600,
                borderRadius: 2,
                py: 1,
                "&:hover": {
                  borderColor: "#6200EE",
                  color: "#6200EE",
                  bgcolor: "#fff",
                },
              }}
            >
              {isSubmitting === "draft" ? (
                <CircularProgress size={16} sx={{ color: "#7C3AED" }} />
              ) : (
                "save as draft"
              )}
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={() => onConfirm?.(buildSelectionData())}
              disabled={!!isSubmitting}
              sx={{
                bgcolor: "#7C3AED",
                textTransform: "lowercase",
                fontWeight: 600,
                borderRadius: 2,
                py: 1,
                "&:hover": { bgcolor: "#6200EE" },
              }}
            >
              {isSubmitting === "confirm" ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                `confirm`
              )}
            </Button>
          </Box>
        </Box>

        {/* ── Right Panel ──────────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Menu name header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
            {/* Menu image avatar */}
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={menuData.menuImage}
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: "#F3EEFF",
                  border: "2px solid #E9E3FF",
                }}
              >
                🍽️
              </Avatar>
              <Box
                sx={{
                  position: "absolute",
                  bottom: -4,
                  right: -4,
                  bgcolor: "#7C3AED",
                  color: "#fff",
                  fontSize: "0.55rem",
                  fontWeight: 700,
                  borderRadius: "50%",
                  width: 22,
                  height: 22,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "2px solid #fff",
                }}
              >
                +3
              </Box>
            </Box>

            {/* Menu name + edit */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  color: "#9ca3af",
                  textTransform: "lowercase",
                  mb: 0.25,
                }}
              >
                {label} name
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {isEditingName ? (
                  <InputBase
                    value={menuName}
                    onChange={(e) => setMenuName(e.target.value)}
                    onBlur={() => setIsEditingName(false)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setIsEditingName(false);
                    }}
                    autoFocus
                    sx={{
                      fontSize: { xs: "0.95rem", sm: "1.1rem" },
                      fontWeight: 700,
                      color: "#111827",
                      flex: 1,
                      textTransform: "lowercase",
                      borderBottom: "2px solid #7C3AED",
                      "& .MuiInputBase-input": { p: 0, pb: 0.25 },
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: { xs: "0.95rem", sm: "1.1rem" },
                      fontWeight: 700,
                      color: "#111827",
                      textTransform: "lowercase",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {menuName}
                  </Typography>
                )}
                <IconButton
                  onClick={() => setIsEditingName(true)}
                  size="small"
                  sx={{ color: "#7C3AED" }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Selection counter */}
          <Typography
            sx={{
              fontSize: "0.75rem",
              color: "#6b7280",
              textTransform: "lowercase",
              mb: 1.5,
            }}
          >
            selected {selectedCatCount} of {totalCategories} categories · {selectedItemCount} of {totalItems} items
          </Typography>

          {/* Menu items grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 2,
              maxHeight: { xs: "auto", md: 460 },
              overflowY: { md: "auto" },
              pr: { md: 0.5 },
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#f1f1f1",
                borderRadius: "2px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#c1c1c1",
                borderRadius: "2px",
                "&:hover": { backgroundColor: "#a8a8a8" },
              },
              scrollbarWidth: "thin",
              scrollbarColor: "#c1c1c1 #f1f1f1",
            }}
          >
            {items.map((item) => {
              const isItemChecked =
                selectedItemIds.get(selectedCategoryId)?.has(item.id) ?? false;
              return (
                <Box key={item.id} sx={{ position: "relative" }}>
                  {/* Checkbox overlay on card top-left */}
                  <Checkbox
                    checked={isItemChecked}
                    onChange={() => handleToggleItem(item.id)}
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      zIndex: 2,
                      bgcolor: "rgba(255,255,255,0.85)",
                      borderRadius: 1,
                      p: 0.25,
                      color: "#7C3AED",
                      "&.Mui-checked": { color: "#7C3AED" },
                    }}
                  />
                  <MenuItemCard
                    item={item}
                    currencySymbol={currencySymbol}
                    onDelete={handleDeleteItem}
                    onItemUpdate={handleItemUpdate}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ConfirmMenu;
